import {useEffect, useState} from 'react'
import axios from 'axios'

const API_URL = process.env.REACT_APP_API_URL

// Admins keep `admin_auth_token` in localStorage even while impersonating a client;
// send it as x-admin-token so the API's callerIsAdmin() authorizes the strip. The
// client JWT (Authorization) is added by the global axios interceptor.
const adminAuthHeaders = (): Record<string, string> => {
  const t = typeof window !== 'undefined' ? localStorage.getItem('admin_auth_token') : null
  return t ? {'x-admin-token': t} : {}
}

type Item = {post_id: string; name: string; before: string; after: string}

// The lines this post loses, in order. Both sides come from the server's own
// transform, so what is listed here is exactly what will be written.
function removedLines(before: string, after: string): string[] {
  const kept = new Set((after || '').split('\n'))
  const out: string[] = []
  for (const line of (before || '').split('\n')) {
    if (!line.trim()) continue
    if (!kept.has(line)) out.push(line)
  }
  return out
}

// Admin-only: take the WhatsApp links out of every post body in one campaign.
// Previews first (the same server call with dry_run on), so the admin approves the
// exact change. Applying snapshots each post's current body to its history, so it
// can be restored per post or rolled back from the revert-by-date panel.
export function WaLinkStripModal({
  campaignId,
  campaignName,
  onClose,
  onApplied,
}: {
  campaignId: string
  campaignName?: string
  onClose: () => void
  onApplied?: () => void
}) {
  const [loading, setLoading] = useState(true)
  const [applying, setApplying] = useState(false)
  const [items, setItems] = useState<Item[]>([])
  const [scanned, setScanned] = useState(0)
  const [vnActive, setVnActive] = useState(false)
  const [msg, setMsg] = useState('')
  const [done, setDone] = useState(false)

  useEffect(() => {
    (async () => {
      setLoading(true)
      setMsg('')
      try {
        const res = await axios.post(
          `${API_URL}/campaign/${campaignId}/wa-links/strip`,
          {dry_run: true},
          {headers: adminAuthHeaders()}
        )
        setItems(res.data.items || [])
        setScanned(res.data.scanned || 0)
        setVnActive(!!res.data.vn_active)
      } catch (e: any) {
        setMsg(e?.response?.data?.error || 'שגיאה בטעינת התצוגה המקדימה')
      }
      setLoading(false)
    })()
  }, [campaignId])

  const apply = async () => {
    setApplying(true)
    setMsg('')
    try {
      const res = await axios.post(
        `${API_URL}/campaign/${campaignId}/wa-links/strip`,
        {dry_run: false},
        {headers: adminAuthHeaders()}
      )
      setDone(true)
      setMsg(`הוסרו לינקים מ-${res.data.changed} פוסטים. הגרסה הקודמת נשמרה בהיסטוריה של כל פוסט.`)
      onApplied?.()
    } catch (e: any) {
      setMsg(e?.response?.data?.error || 'השמירה נכשלה')
    }
    setApplying(false)
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 1100,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: 20,
        overflowY: 'auto',
      }}
    >
      <div onClick={(e) => e.stopPropagation()} className='card' dir='rtl' style={{maxWidth: 760, width: '100%', margin: 'auto'}}>
        <div className='card-header align-items-center'>
          <h3 className='card-title mb-0'>
            הסרת לינקי וואטסאפ מתוכן הפוסטים
            <span className='badge badge-light-danger fs-9 ms-1'>אדמין</span>
          </h3>
          <button className='btn btn-sm btn-icon btn-light' onClick={onClose}>✕</button>
        </div>

        <div className='card-body'>
          <div className='text-muted fs-7 mb-4'>
            קמפיין: <strong>{campaignName || campaignId}</strong> — מוסר כל לינק וואטסאפ מתוכן הפוסט
            (wa.me וגם לינקי המעקב katzr.net / l.fbzipper.com), כולל שורת הכותרת שנשארת ריקה.
            מספרי טלפון נשארים כמו שהם.
          </div>

          {loading && <div className='text-muted'>טוען תצוגה מקדימה…</div>}

          {!loading && vnActive && !done && (
            <div className='alert alert-warning py-3 fs-7'>
              ⚠️ לחשבון הזה יש מספר וירטואלי פעיל. לינקי המעקב הם מה שמשייך לידים מוואטסאפ לפוסט —
              אחרי ההסרה פניות וואטסאפ מהפוסטים האלה לא ייספרו.
            </div>
          )}

          {!loading && !items.length && !done && (
            <div className='text-muted'>אין לינקי וואטסאפ בתוכן הפוסטים של הקמפיין ({scanned} פוסטים נבדקו).</div>
          )}

          {!loading && !!items.length && (
            <>
              <div className='fw-bold mb-3'>
                {done ? 'הוסר מ-' : 'ישתנו '}
                {items.length} מתוך {scanned} פוסטים
              </div>
              <div style={{maxHeight: '50vh', overflowY: 'auto'}}>
                {items.map((it) => (
                  <div key={it.post_id} className='border rounded p-3 mb-2'>
                    <div className='fw-semibold fs-7 mb-1'>{it.name || '(ללא שם)'}</div>
                    {removedLines(it.before, it.after).map((line, i) => (
                      <div
                        key={i}
                        className='fs-8'
                        style={{
                          background: '#ffeaea',
                          color: '#b00020',
                          textDecoration: 'line-through',
                          padding: '2px 6px',
                          borderRadius: 4,
                          marginBottom: 2,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {line}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </>
          )}

          {!!msg && <div className={`mt-4 fs-7 ${done ? 'text-success' : 'text-danger'}`}>{msg}</div>}
        </div>

        <div className='card-footer d-flex justify-content-end gap-2 py-4'>
          <button className='btn btn-light btn-sm' onClick={onClose}>{done ? 'סגור' : 'ביטול'}</button>
          {!done && (
            <button
              className='btn btn-danger btn-sm'
              onClick={apply}
              disabled={loading || applying || !items.length}
              title={!items.length ? 'אין מה להסיר' : 'ההסרה נשמרת בהיסטוריה וניתנת לשחזור'}
            >
              {applying ? 'מסיר…' : `הסר מ-${items.length} פוסטים`}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default WaLinkStripModal
